import {
  X,
  Calendar,
  User,
  TrendingUp,
} from "lucide-react";

const statusStyles = {
  Active: "bg-green-100 text-green-700",
  Planning: "bg-yellow-100 text-yellow-700",
  Completed: "bg-blue-100 text-blue-700",
  "On Hold": "bg-red-100 text-red-700",
  Draft: "bg-slate-100 text-slate-700",
  Cancelled: "bg-red-100 text-red-700",
};

const priorityStyles = {
  Critical: "bg-red-100 text-red-700",
  High: "bg-orange-100 text-orange-700",
  Medium: "bg-yellow-100 text-yellow-700",
  Low: "bg-green-100 text-green-700",
};

const DrawerHeader = ({
  project,
  employees = [],
  employeesLoading = false,
  progress = 0,
  progressLoading = false,
  onClose,
}) => {
  if (!project) return null;

  // ============================================================
  // PROJECT NAME
  // ============================================================

  const projectName =
    project.project_name ||
    project.projectName ||
    "Unnamed Project";

  // ============================================================
  // PROJECT CODE
  // ============================================================

  const projectCode =
    project.project_code ||
    project.projectCode ||
    "-";

  // ============================================================
  // STATUS
  // ============================================================

  const status = project.status || "Draft";

  // ============================================================
  // PRIORITY
  // ============================================================

  const priority = project.priority || "Medium";

  // ============================================================
  // PROJECT MANAGER
  // ============================================================

  const managerUuid =
    project.project_manager_employee_uuid ||
    project.project_manager_uuid ||
    project.project_manager?.uuid ||
    "";

  const projectManagerEmployee = employees.find((employee) => {
    const employeeUuid =
      employee.uuid ||
      employee.employee_uuid ||
      employee.id;

    return String(employeeUuid) === String(managerUuid);
  });

  // ============================================================
  // MANAGER NAME
  // ============================================================

  const projectManagerName =
    project.project_manager_name ||
    project.project_manager?.name ||
    projectManagerEmployee?.name ||
    projectManagerEmployee?.employee_name ||
    projectManagerEmployee?.full_name ||
    projectManagerEmployee?.employeeName ||
    (
      projectManagerEmployee?.first_name &&
      projectManagerEmployee?.last_name
        ? `${projectManagerEmployee.first_name} ${projectManagerEmployee.last_name}`
        : ""
    ) ||
    (
      projectManagerEmployee?.firstName &&
      projectManagerEmployee?.lastName
        ? `${projectManagerEmployee.firstName} ${projectManagerEmployee.lastName}`
        : ""
    ) ||
    (employeesLoading ? "Loading..." : "Not assigned");

  // ============================================================
  // START DATE
  // ============================================================

  const rawStartDate =
    project.planned_start_date ||
    project.startDate ||
    "";

  const formatDate = (dateValue) => {
    if (!dateValue) {
      return "Not specified";
    }

    const date = new Date(dateValue);

    if (Number.isNaN(date.getTime())) {
      return "Not specified";
    }

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const startDate = formatDate(rawStartDate);

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="border-b border-slate-200 bg-white px-6 py-6">

      {/* ======================================================
          TOP
      ====================================================== */}

      <div className="flex items-start justify-between">

        <div>
          <h2 className="text-2xl font-bold text-slate-800">
            {projectName}
          </h2>

          <p className="mt-1 text-sm text-slate-500">
            {projectCode}
          </p>
        </div>

        <button
          type="button"
          onClick={onClose}
          className="rounded-lg p-2 transition hover:bg-slate-100"
        >
          <X size={20} />
        </button>

      </div>

      {/* ======================================================
          STATUS + PRIORITY
      ====================================================== */}

      <div className="mt-5 flex gap-3">

        <span
          className={`rounded-full px-3 py-1 text-xs font-semibold ${
            statusStyles[status] ||
            "bg-slate-100 text-slate-700"
          }`}
        >
          {status}
        </span>

        <span
          className={`rounded-full px-3 py-1 text-xs font-semibold ${
            priorityStyles[priority] ||
            "bg-slate-100 text-slate-700"
          }`}
        >
          {priority}
        </span>

      </div>

      {/* ======================================================
          QUICK INFORMATION
      ====================================================== */}

      <div className="mt-7 grid grid-cols-1 gap-5 md:grid-cols-3">

        {/* ====================================================
            MANAGER
        ==================================================== */}

        <div className="flex items-center gap-3">

          <div className="flex h-8 w-8 items-center justify-center">
            <User
              size={18}
              className="text-slate-400"
            />
          </div>

          <div>
            <p className="text-xs text-slate-500">
              Manager
            </p>

            <p className="text-sm font-medium text-slate-800">
              {projectManagerName}
            </p>
          </div>

        </div>

        {/* ====================================================
            START DATE
        ==================================================== */}

        <div className="flex items-center gap-3">

          <div className="flex h-8 w-8 items-center justify-center">
            <Calendar
              size={18}
              className="text-slate-400"
            />
          </div>

          <div>
            <p className="text-xs text-slate-500">
              Start Date
            </p>

            <p className="text-sm font-medium text-slate-800">
              {startDate}
            </p>
          </div>

        </div>

        {/* ====================================================
            PROGRESS
        ==================================================== */}

        <div className="flex items-center gap-3">

          <div className="flex h-8 w-8 items-center justify-center">
            <TrendingUp
              size={18}
              className="text-slate-400"
            />
          </div>

          <div>
            <p className="text-xs text-slate-500">
              Progress
            </p>

            <p className="text-sm font-medium text-slate-800">
              {progressLoading
                ? "..."
                : `${progress}%`}
            </p>
          </div>

        </div>

      </div>

    </div>
  );
};

export default DrawerHeader;